import { Component, Input, OnChanges } from "@angular/core";
import { CommonModule } from "@angular/common";
import { ImageUrlService } from "../../services/image-url.service";

@Component({
  selector: "lib-image-gallery",
  template: `
    <div class="image-gallery">
      <h3 class="gallery-title">Images</h3>

      <div class="image-grid">
        <div
          *ngFor="let imageUrl of imageUrls; trackBy: trackImage"
          class="image-item"
          draggable="true"
          (dragstart)="onDragStart($event, imageUrl)"
        >
          <img
            [src]="imageUrl"
            [alt]="'Gallery image'"
            class="gallery-image"
            loading="lazy"
          />
          <div class="image-overlay">
            <span class="drag-hint">Drag to canvas</span>
          </div>
        </div>
      </div>

      <div *ngIf="imageUrls.length === 0" class="empty-state">
        <p>No images available</p>
        <small>Add image URLs to see them here</small>
      </div>
    </div>
  `,
  styles: [
    `
      :host {
        /* CSS variables for consistent styling */
        --canvas-primary: #232459;
        --canvas-bg-primary: #ffffff;
        --canvas-border-light: #e2e8f0;
        --canvas-gray-800: #1f2937;
        --canvas-gray-100: #f3f4f6;
        --canvas-gray-500: #6b7280;
        --canvas-space-2: 0.5rem;
        --canvas-space-3: 0.75rem;
        --canvas-space-4: 1rem;
        --canvas-text-sm: 0.875rem;
        --canvas-text-lg: 1.125rem;
        --canvas-font-semibold: 600;
        --canvas-font-medium: 500;
        --canvas-font-family: "Google Sans", -apple-system, BlinkMacSystemFont,
          "Segoe UI", Roboto, sans-serif;
        --canvas-radius: 0.375rem;
        --canvas-transition: 200ms ease;
        --canvas-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
        --canvas-shadow-md: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
        display: block;
        width: 100%;
        height: 100%;
      }

      .image-gallery {
        width: 100%;
        height: 100%;
        background: white;
        padding: 0.75rem;
        overflow-y: auto;
        font-family: "Google Sans", -apple-system, BlinkMacSystemFont,
          "Segoe UI", Roboto, sans-serif;
      }

      .gallery-title {
        font-size: 1.125rem;
        font-weight: 600;
        margin: 0 0 1rem 0;
        color: #1f2937;
        padding-bottom: 0.75rem;
        border-bottom: 1px solid #e2e8f0;
      }

      .image-grid {
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
        gap: 0.75rem;
        margin-top: 0.75rem;
      }

      .image-item {
        position: relative;
        border-radius: 0.375rem;
        overflow: hidden;
        cursor: grab;
        transition: all 200ms ease;
        background: #f3f4f6;
        aspect-ratio: 4/3;
        border: 2px solid transparent;
      }

      .image-item:hover {
        transform: translateY(-2px);
        box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
        border-color: #232459;
      }

      .image-item:active {
        cursor: grabbing;
        transform: scale(0.95);
      }

      .gallery-image {
        width: 100%;
        height: 100%;
        object-fit: cover;
        display: block;
      }

      .image-overlay {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: rgba(35, 36, 89, 0.9);
        display: flex;
        align-items: center;
        justify-content: center;
        opacity: 0;
        transition: opacity var(--canvas-transition);
      }

      .image-item:hover .image-overlay {
        opacity: 1;
      }

      .drag-hint {
        color: white;
        font-size: var(--canvas-text-sm);
        font-weight: var(--canvas-font-medium);
        text-align: center;
      }

      .empty-state {
        text-align: center;
        padding: var(--canvas-space-4);
        color: var(--canvas-gray-500);
        margin-top: var(--canvas-space-4);
      }

      .empty-state p {
        margin: 0 0 var(--canvas-space-2) 0;
        font-weight: var(--canvas-font-medium);
      }

      .empty-state small {
        font-size: var(--canvas-text-sm);
      }

      /* Mobile responsive */
      @media (max-width: 640px) {
        .image-grid {
          grid-template-columns: repeat(auto-fill, minmax(100px, 1fr));
          gap: var(--canvas-space-2);
        }

        .gallery-title {
          font-size: var(--canvas-text-base);
        }

        .drag-hint {
          font-size: 10px;
        }
      }

      /* Scrollbar styling */
      .image-gallery::-webkit-scrollbar {
        width: 6px;
      }

      .image-gallery::-webkit-scrollbar-track {
        background: var(--canvas-gray-100);
        border-radius: var(--canvas-radius);
      }

      .image-gallery::-webkit-scrollbar-thumb {
        background: var(--canvas-gray-300);
        border-radius: var(--canvas-radius);
      }

      .image-gallery::-webkit-scrollbar-thumb:hover {
        background: var(--canvas-gray-400);
      }
    `,
  ],
})
export class ImageGalleryComponent implements OnChanges {
  @Input() imageUrls: string[] = [
    "https://images.pexels.com/photos/1779487/pexels-photo-1779487.jpeg?auto=compress&cs=tinysrgb&w=400",
    "https://images.pexels.com/photos/1462935/pexels-photo-1462935.jpeg?auto=compress&cs=tinysrgb&w=400",
    "https://images.pexels.com/photos/1323550/pexels-photo-1323550.jpeg?auto=compress&cs=tinysrgb&w=400",
    "https://images.pexels.com/photos/1563356/pexels-photo-1563356.jpeg?auto=compress&cs=tinysrgb&w=400",
    "https://images.pexels.com/photos/1587927/pexels-photo-1587927.jpeg?auto=compress&cs=tinysrgb&w=400",
    "https://images.pexels.com/photos/1366919/pexels-photo-1366919.jpeg?auto=compress&cs=tinysrgb&w=400",
  ];

  constructor(private imageUrlService: ImageUrlService) {
    // Register all images on component initialization
    this.registerImages();
  }

  ngOnChanges(): void {
    // Re-register images if the input changes
    this.registerImages();
  }

  private registerImages(): void {
    this.imageUrlService.registerImages(this.imageUrls);
  }

  onDragStart(event: DragEvent, imageUrl: string): void {
    // Get the path instead of the full URL for drag data
    const imagePath = this.imageUrlService.extractImagePath(imageUrl);

    const dragData = {
      type: "image",
      url: imagePath, // Store only the path
    };

    event.dataTransfer?.setData("application/json", JSON.stringify(dragData));
  }

  trackImage(index: number, imageUrl: string): string {
    return imageUrl;
  }
}
